import React, { useState, useEffect, useRef, useMemo } from "react";
import * as echarts from 'echarts';
import DashboardMainContainer from "../common/Containers/DashboardMainContainer";
import {
  AdminPanelTitle,
  AdminMetric,
  AdminSupportingText,
  AdminMetadataText,
} from '../../sections/AdminDashboard/shared/adminUx';
import { AdminWidgetHeader, AdminWidgetPeriodFilter } from './AdminWidgetChrome';

// Stable datasets per period
const PROFILE_DATA_BY_PERIOD = {
  '7 Days': {
    views: 8416,
    saves: 720,
    shares: 138,
    viewsSeries: [90, 252, 201, 434, 290, 489, 280],
    savesSeries: [60, 132, 101, 164, 150, 230, 210],
    sharesSeries: [20, 32, 21, 34, 30, 30, 40],
    uniquePercentage: 65,
    repeatPercentage: 35,
    deviceBreakdown: [
      { value: 45, name: 'Mobile' },
      { value: 30, name: 'Desktop' },
      { value: 25, name: 'Tablet' }
    ]
  },
  '14 Days': {
    views: 16832,
    saves: 1440,
    shares: 276,
    viewsSeries: [1200, 1500],
    savesSeries: [800, 1000],
    sharesSeries: [200, 250],
    uniquePercentage: 63,
    repeatPercentage: 37,
    deviceBreakdown: [
      { value: 46, name: 'Mobile' },
      { value: 31, name: 'Desktop' },
      { value: 23, name: 'Tablet' }
    ]
  },
  '30 Days': {
    views: 32150,
    saves: 3120,
    shares: 640,
    viewsSeries: [1200, 800, 1500, 1300],
    savesSeries: [600, 700, 800, 1020],
    sharesSeries: [150, 170, 160, 160],
    uniquePercentage: 62,
    repeatPercentage: 38,
    deviceBreakdown: [
      { value: 48, name: 'Mobile' },
      { value: 32, name: 'Desktop' },
      { value: 20, name: 'Tablet' }
    ]
  },
  '90 Days': {
    views: 97200,
    saves: 8600,
    shares: 1900,
    viewsSeries: [3200, 3400, 3100],
    savesSeries: [2600, 3000, 3000],
    sharesSeries: [600, 630, 670],
    uniquePercentage: 60,
    repeatPercentage: 40,
    deviceBreakdown: [
      { value: 46, name: 'Mobile' },
      { value: 36, name: 'Desktop' },
      { value: 18, name: 'Tablet' }
    ]
  },
  '1 Year': {
    views: 392000,
    saves: 32000,
    shares: 7600,
    viewsSeries: [9000, 10000, 9800, 10400],
    savesSeries: [7000, 8000, 7800, 8200],
    sharesSeries: [1800, 1900, 1950, 1950],
    uniquePercentage: 58,
    repeatPercentage: 42,
    deviceBreakdown: [
      { value: 44, name: 'Mobile' },
      { value: 38, name: 'Desktop' },
      { value: 18, name: 'Tablet' }
    ]
  }
};

const BusinessProfileWidget = ({
  className = 'masonry-item w-[440px] h-[300px] flex flex-col',
}) => {
  const [timeFilter, setTimeFilter] = useState('7 Days');
  const chartRef1 = useRef(null);
  const chartRef2 = useRef(null);
  const chartRef3 = useRef(null);
  const chartRef4 = useRef(null);
  const chartRef5 = useRef(null);

  const profileData = useMemo(() => PROFILE_DATA_BY_PERIOD[timeFilter] || PROFILE_DATA_BY_PERIOD['7 Days'], [timeFilter]);

  useEffect(() => {
    // Profile Views Mini Chart
    if (chartRef1.current) {
      const chart = echarts.getInstanceByDom(chartRef1.current) || echarts.init(chartRef1.current);
      const option = {
        grid: { top: 0, right: 0, bottom: 0, left: 0 },
        xAxis: { show: false, type: 'category', data: Array.from({ length: profileData.viewsSeries.length }, (_, i) => i + 1), boundaryGap: false },
        yAxis: { show: false, type: 'value' },
        series: [{
          data: profileData.viewsSeries,
          type: 'line',
          smooth: true,
          symbol: 'none',
          lineStyle: { color: '#2E00FF', width: 2 },
          areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(123, 98, 217, 0.4)' }] } }
        }]
      };
      chart.setOption(option, true);
    }
  }, [timeFilter, profileData]);

  useEffect(() => {
    // Profile Saves Mini Chart
    if (chartRef2.current) {
      const chart = echarts.getInstanceByDom(chartRef2.current) || echarts.init(chartRef2.current);
      const option = {
        grid: { top: 0, right: 0, bottom: 0, left: 0 },
        xAxis: { show: false, type: 'category', data: Array.from({ length: profileData.savesSeries.length }, (_, i) => i + 1), boundaryGap: false },
        yAxis: { show: false, type: 'value' },
        series: [{
          data: profileData.savesSeries,
          type: 'line',
          smooth: true,
          symbol: 'none',
          lineStyle: { color: '#2E00FF', width: 2 },
          areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(123, 98, 217, 0.4)' }] } }
        }]
      };
      chart.setOption(option, true);
    }
  }, [timeFilter, profileData]);

  useEffect(() => {
    // Profile Shares Mini Chart
    if (chartRef3.current) {
      const chart = echarts.getInstanceByDom(chartRef3.current) || echarts.init(chartRef3.current);
      const option = {
        grid: { top: 0, right: 0, bottom: 0, left: 0 },
        xAxis: { show: false, type: 'category', data: Array.from({ length: profileData.sharesSeries.length }, (_, i) => i + 1), boundaryGap: false },
        yAxis: { show: false, type: 'value' },
        series: [{
          data: profileData.sharesSeries,
          type: 'line',
          smooth: true,
          symbol: 'none',
          lineStyle: { color: '#2E00FF', width: 2 },
          areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(123, 98, 217, 0.4)' }] } }
        }]
      };
      chart.setOption(option, true);
    }
  }, [timeFilter, profileData]);

  useEffect(() => {
    // Unique vs Repeat Donut Chart
    if (chartRef4.current) {
      const chart = echarts.getInstanceByDom(chartRef4.current) || echarts.init(chartRef4.current);
      const option = {
        tooltip: { trigger: 'item' },
        series: [{
          type: 'pie',
          radius: ['60%', '90%'],
          center: ['50%', '50%'],
          data: [
            { value: profileData.uniquePercentage, name: 'Unique', itemStyle: { color: '#2E00FF' } },
            { value: profileData.repeatPercentage, name: 'Repeat', itemStyle: { color: '#E9D5FF' } }
          ],
          label: { show: false },
          labelLine: { show: false }
        }]
      };
      chart.setOption(option, true);
    }
  }, [timeFilter, profileData]);

  useEffect(() => {
    // Views over time Pie Chart
    if (chartRef5.current) {
      const chart = echarts.getInstanceByDom(chartRef5.current) || echarts.init(chartRef5.current);
      const option = {
        legend: {
          orient: 'horizontal',
          bottom: 0,
          left: 'center',
          itemGap: 8,
          itemWidth: 12,
          itemHeight: 12,
          textStyle: {
            color: '#232A2E',
            fontSize: 11,
            rich: {
              label: { fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"', fontSize: 12, fontWeight: 500, color: '#232A2E' },
              value: { fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"', fontSize: 11, fontWeight: 500, color: '#232A2E' }
            }
          },
          itemStyle: {
            borderWidth: 1
          },
          icon: 'circle',
          align: 'auto',
          formatter: function(name) {
            const item = profileData.deviceBreakdown.find(d => d.name === name);
            const percent = item ? item.value : '';
            return `{label|${name}}  {value|${percent}%}`;
          }
        },
        series: [{
          type: 'pie',
          radius: '90%',
          center: ['50%', '29%'],
          data: profileData.deviceBreakdown.map(item => ({
            ...item,
            itemStyle: { 
              color: item.name === 'Mobile' ? '#7B62D9' : item.name === 'Desktop' ? '#2E00FF' : '#03A62C' 
            }
          })),
          label: { show: false },
          labelLine: { show: false }
        }]
      };
      chart.setOption(option, true);
    }
  }, [timeFilter, profileData]);

  return (
    <DashboardMainContainer className={className}>
      <AdminWidgetHeader title="Business Profile Overview">
        <AdminWidgetPeriodFilter period={timeFilter} onPeriodChange={setTimeFilter} />
      </AdminWidgetHeader>
      
      <div className="flex-1 flex p-4 gap-2">
        {/* 1st Column - Profile Views, Contact Clicks, Quote received */}
        <div className="w-2/3 space-y-2">
        <div className="flex items-start justify-between">
            <div className="space-y-0">
              <AdminPanelTitle className="text-offblack">Profile Views</AdminPanelTitle>
              <AdminMetric size="medium" className="text-offblack tabular-nums">
                {profileData.views.toLocaleString()}
              </AdminMetric>
            </div>
            <div ref={chartRef1} className="w-44 h-14"></div>
          </div>

          <div className="flex gap-2">
          
          <div className="flex flex-col gap-1 w-full">
            <div className="space-y-0 w-full">
                <AdminPanelTitle className="text-offblack">Contact Clicks</AdminPanelTitle>
                <AdminMetric size="medium" className="text-offblack tabular-nums">
                  {profileData.saves.toLocaleString()}
                </AdminMetric>
                <div ref={chartRef2} className="w-full h-6"></div>
            </div>
            
            <div className="space-y-0">
                <AdminPanelTitle className="text-offblack">Quote received</AdminPanelTitle>
                <AdminMetric size="medium" className="text-offblack tabular-nums">
                  {profileData.shares.toLocaleString()}
                </AdminMetric>
                <div ref={chartRef3} className="w-full h-6"></div>
            </div>
          </div>

            <div className="flex flex-col items-center gap-0 w-full mt-2 border-l border-lightgray">
                <AdminSupportingText className="text-offblack mb-2 block">Unique vs Repeat</AdminSupportingText>
                <div className="relative">
                    <div ref={chartRef4} className="w-24 h-24"></div>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <AdminMetric size="small" className="text-offblack leading-none tabular-nums">
                          {profileData.uniquePercentage}%
                        </AdminMetric>
                        <AdminPanelTitle className="text-offblack">Unique</AdminPanelTitle>
                    </div>
                </div>
                <div className="flex flex-row w-full items-center justify-around mt-1">
                    <AdminMetric size="micro" className="text-offblack tabular-nums">
                      {profileData.repeatPercentage}%
                    </AdminMetric>
                    <AdminMetadataText className="text-offblack">Repeat</AdminMetadataText>
                </div>
            </div>

          </div>

        </div>

                 {/* 3rd Column - Views over time */}
         <div className="w-1/3 flex flex-col items-center justify-start border-l border-lightgray">
           <AdminSupportingText className="text-offblack mb-2 block">Views Over Time</AdminSupportingText>
           <div ref={chartRef5} className="w-28 h-48"></div>
           {/* Static legend commented out - using ECharts built-in legend instead */}
         </div>
      </div>
    </DashboardMainContainer>
  );
};

export default BusinessProfileWidget;
